package com.steamstreet.vegasful.browser.account.intelligence

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.loginPanel
import com.steamstreet.vegasful.browser.components.autoGrow
import com.steamstreet.vegasful.browser.components.typewriterPlaceholders
import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.graphql.api.ConversationInput
import com.steamstreet.vegasful.web.ClientController
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.css.*
import kotlinx.dom.clear
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.Element
import org.w3c.dom.HTMLTextAreaElement

private const val intelligenceInputId: String = "intelligence-input"
private const val statusId: String = "intelligence-status"


/**
 * A list of predefined input hints or suggestions for the user when interacting with the intelligence system.
 * These hints provide examples of queries users can ask, such as event recommendations or venue suggestions.
 * The list supports dynamic placeholder functionality in the text area input within the intelligence interface.
 */
private val inputHints = listOf(
    "What’s happening tonight?",
    "Any rooftop shows this weekend?",
    "Find me something low-key and fun.",
    "Where’s a good comedy show?",
    "Show me what’s electric in Vegas tonight."
)


/**
 * The `Intelligence` class handles the main interaction and behavior
 * of the Vegasful Intelligence feature. It provides a dynamic UI for
 * logged-in and logged-out users and facilitates backend queries related
 * to user input.
 *
 * @property element The root DOM element where the Intelligence feature will be rendered.
 * @property scope The coroutine scope for managing asynchronous operations related to the class tasks.
 *
 * Implements the `ClientController` interface to define a `run` method for initializing the UI and handling user interactions.
 */
class Intelligence(private val element: Element, private val scope: CoroutineScope) : ClientController {
    private val httpClient = HttpClient()

    override suspend fun run() {
        element.append {
            if (!Account.isLoggedIn()) {
                div {
                    h1 {
                        css {
                            fontSize = (1.75).rem
                            marginBottom = (1).rem
                        }
                        +"Login"
                    }
                    div {
                        css {
                            fontWeight = FontWeight.w300
                            marginBottom = (2).rem
                        }
                        +"To use Vegasful Intelligence, you must have a Vegasful account"
                    }
                    div {
                        css {
                            maxWidth = 484.px
                            margin = Margin(0.px, LinearDimension.auto)
                        }
                        loginPanel()
                    }
                }
            } else {
                div {
                    textArea {
                        this.id = intelligenceInputId
                        placeholder =
                            "Ask anything about what you'd like to do... (e.g. 'Are there any jazz or soul events coming up?')"
                        css {
                            width = 100.pct
                            height = 200.px
                            padding = Padding(10.px)
                            borderRadius = 10.px
                        }
                    }

                    intelligenceButton("Ask Vegasful") {
                        val query = (document.getElementById(intelligenceInputId) as? HTMLTextAreaElement)?.value
                        if (query != null) {
                            scope.launch {
                                executeQuery(query)
                            }
                        }
                    }
                }
            }
        }

        val textInput = document.getElementById(intelligenceInputId) as? HTMLTextAreaElement
        textInput?.autoGrow(2)
        textInput?.typewriterPlaceholders(scope, inputHints)
    }


    private suspend fun executeQuery(query: String) {
        element.clear()
        element.append {
            div {
                p {
                    css {
                        fontWeight = FontWeight.w600
                        fontSize = 18.px
                        textAlign = TextAlign.left
                    }
                    +query
                }
                div {
                    id = statusId
                    +"Analyzing Vegas events..."
                }
            }
        }

        console.log("Running query: $query")
        val id = GraphQL.client {
            conversations {
                start(ConversationInput(query)) {
                    id
                }
            }
        }.conversations.start.id

        // Poll for conversation results using simple HTTP polling
        pollForConversation(id)
    }

    private suspend fun pollForConversation(id: String) {
        val statusElement = document.getElementById(statusId)!!

        try {
            // Poll the conversation endpoint until complete
            while (true) {
                delay(1000) // Poll every second

                // Fetch HTML from the server using Ktor
                val response = httpClient.get("/conversations/$id")

                // Check for HTTP errors
                if (!response.status.isSuccess()) {
                    console.error("HTTP error: ${response.status.value} ${response.status.description}")
                    val errorText = response.bodyAsText()
                    console.error("Error response:", errorText)
                    statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred while processing your request. Please try again.</p>"
                    break
                }

                val html = response.bodyAsText()

                // Update the status div with the server-rendered HTML
                statusElement.innerHTML = html

                // If the HTML doesn't contain "conversation-status", it's complete
                if (!html.contains("conversation-status")) {
                    break
                }
            }
        } catch (e: Exception) {
            console.error("Error polling conversation:", e)
            statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred. Please try again.</p>"
        }
    }

    /**
     * Renders an interactive button with specific styling and behavior tied to a click event.
     *
     * @param name The display name of the button.
     * @param onClick The action to be executed when the button is clicked.
     */
    private fun FlowContent.intelligenceButton(name: String, onClick: () -> Unit) {
        a(href = "#") {
            css {
                width = 100.pct
                display = Display.flex
                this.color = Color.white
                backgroundColor = VegasfulStyles.purple
                fontSize = 18.px
                fontWeight = FontWeight.w600
                padding = Padding(8.px)
                borderRadius = 5.px
                marginTop = 8.px

            }
            onClickFunction = {
                onClick()
            }
            span {
                css {
                    flexGrow = 1.0
                    textAlign = TextAlign.center
                }
                +name
            }
        }
    }
}
